/**
 * useUserRole — Fetches the current user's role from Supabase.
 * Returns 'admin', 'medewerker', 'callcenter', or null (nog onbekend).
 *
 * Semantiek (les uit de Supabase-outage 2026-07-02):
 * - Bevestigd géén rij in app_user_roles → default 'callcenter'.
 * - Query FAALT (Supabase down, netwerk, verlopen sessie) → rol blijft wat hij
 *   was (null bij eerste load) en we blijven met backoff opnieuw proberen.
 *   Nooit stil degraderen: dat gaf tijdens de outage een half menu voor admins.
 *
 * De fetch + retry-lus leeft op module-niveau en wordt gedeeld door alle
 * componenten die deze hook gebruiken (12+), zodat niet elke mount zijn eigen
 * query en retry-lus start.
 */

import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAuthStore } from '../stores';

export type UserRole = 'admin' | 'medewerker' | 'callcenter';

interface RoleState {
  role: UserRole | null;
  isLoading: boolean;
  /** Mislukte pogingen op rij sinds de laatste geslaagde fetch (werkbank #99). */
  mislukt: number;
}

/** Vanaf zoveel mislukte pogingen toont de app een balk met Opnieuw laden. */
export const ROL_MISLUKT_DREMPEL = 3;

const RETRY_MAX_MS = 30_000;

let storeEmail: string | null = null;
let storeState: RoleState = { role: null, isLoading: true, mislukt: 0 };
let retryTimer: ReturnType<typeof setTimeout> | null = null;
const listeners = new Set<(s: RoleState) => void>();

function publish(next: RoleState) {
  if (next.role === storeState.role && next.isLoading === storeState.isLoading && next.mislukt === storeState.mislukt) return;
  storeState = next;
  listeners.forEach((l) => l(next));
}

async function fetchRole(email: string, attempt: number): Promise<void> {
  const { data, error } = await supabase
    .from('app_user_roles')
    .select('role, display_name')
    .eq('user_email', email)
    .maybeSingle();

  if (email !== storeEmail) return; // gebruiker gewisseld tijdens de fetch

  if (error) {
    // Uitkomst onbekend: huidige rol vasthouden en opnieuw proberen. Zodra
    // Supabase weer antwoordt herstelt de rol (en dus het menu) vanzelf.
    // Albert 11-10: leeg menu zonder uitleg, de stille retry hielp niet; pas een
    // laptop-herstart. Tel de mislukkingen zodat AppLayout een balk kan tonen.
    publish({ role: storeState.role, isLoading: false, mislukt: attempt + 1 });
    const delay = Math.min(RETRY_MAX_MS, 1000 * 2 ** attempt);
    retryTimer = setTimeout(() => void fetchRole(email, attempt + 1), delay);
    return;
  }

  if (!data) {
    // Bevestigd: geen rij voor dit e-mailadres.
    publish({ role: 'callcenter', isLoading: false, mislukt: 0 });
    return;
  }

  publish({ role: data.role as UserRole, isLoading: false, mislukt: 0 });
  if (data.display_name) {
    const currentUser = useAuthStore.getState().user;
    if (currentUser && currentUser.displayName !== data.display_name) {
      useAuthStore.setState({
        user: { ...currentUser, displayName: data.display_name },
      });
    }
  }
}

function ensureFetch(email: string | null) {
  if (email === storeEmail) return;
  if (retryTimer) {
    clearTimeout(retryTimer);
    retryTimer = null;
  }
  storeEmail = email;
  if (!email) {
    publish({ role: null, isLoading: false, mislukt: 0 });
    return;
  }
  publish({ role: null, isLoading: true, mislukt: 0 });
  void fetchRole(email, 0);
}

/** Nu opnieuw proberen (knop in de balk): wist de backoff-timer en start bij poging 0. */
export function herlaadRol(): void {
  if (!storeEmail) return;
  if (retryTimer) {
    clearTimeout(retryTimer);
    retryTimer = null;
  }
  publish({ role: storeState.role, isLoading: true, mislukt: storeState.mislukt });
  void fetchRole(storeEmail, 0);
}

export function useUserRole(): { role: UserRole | null; isLoading: boolean; mislukt: number } {
  const user = useAuthStore((s) => s.user);
  const [state, setState] = useState<RoleState>(storeState);

  useEffect(() => {
    listeners.add(setState);
    ensureFetch(user?.email ?? null);
    setState(storeState);
    return () => {
      listeners.delete(setState);
    };
  }, [user?.email]);

  return state;
}
